import {
  Position,
  type InputDescription,
  type IntegrationDescription,
} from "@smartsuite/automation-engine-api";
import { toJS } from "mobx";
import type React from "react";
import { useEffect } from "react";
import type {
  ActionCandidate,
  InputCandidate,
  TriggerCandidate,
} from "../../../../types/candidate";
import { StyledReactSelectFieldContainer } from "./SelectEditor/InputSelectStyles";
import { StyledInputWrapper } from "./style";
import { shouldHideTeams } from "./utils";
import { TestTriggerEditor } from "./FieldEditor/FieldEditorRow/fields/TestTriggerEditor";
import { SelectEditor } from "./SelectEditor";
import { ReadOnlyEditor } from "./FieldEditor/FieldEditorRow/fields/ReadOnlyEditor";
import { UrlReadOnlyEditor } from "./FieldEditor/FieldEditorRow/fields/UrlReadOnlyEditor";
import { KeyValueEditor } from "./FieldEditor/FieldEditorRow/fields/KeyValueEditor";
import { FieldEditorRow } from "./FieldEditor/FieldEditorRow";
import { MemberEditor } from "./MemberEditor";
import { PhoneEditor } from "./FieldEditor/FieldEditorRow/fields/PhoneEditor";
import { FileEditor } from "./FieldEditor/FieldEditorRow/fields/FileEditor";
import { FieldsEditor } from "./FieldEditor/FieldEditorRow/fields/FieldsEditor";
import { ScheduleEditor } from "./FieldEditor/FieldEditorRow/fields/ScheduleEditor";
import { CheckboxEditor } from "./FieldEditor/FieldEditorRow/fields/CheckboxEditor";
import { MergeActionsEditor } from "./MergeActionsEditor";
import { LimitSortEditor } from "./FieldEditor/FieldEditorRow/fields/LimitSortEditor";
import { JsonEditorWrapper } from "./FieldEditor/FieldEditorRow/fields/JsonEditor/JsonEditorWrapper";
import { DynamicSelectEditor } from "./DynamicSelectEditor";
import { UnknownInputEditor } from "./UnknownInputEditor";

export const InputForm: React.FC<{
  input: InputCandidate;
  nextInput?: InputCandidate;
  descriptions: InputDescription[];
  trigger: TriggerCandidate;
  actions: ActionCandidate[];
  actionHash: string;
  /** The action these inputs belong to. Required whenever `actions` is the
   * upstream-scoped list (spec 8.1) -- that list deliberately EXCLUDES the
   * current action, so it can no longer be resolved out of `actions` by hash
   * (BUG-21280: it resolved to undefined and every editor lost its
   * action-output menus). Callers passing the full action list may omit it. */
  currentAction?: ActionCandidate;
  integrations: IntegrationDescription[];
  loading?: boolean;
  onChange: (input: InputCandidate) => void;
  showValidationError?: boolean;
}> = ({
  input,
  nextInput,
  descriptions,
  trigger,
  actions,
  actionHash,
  currentAction: currentActionProp,
  integrations,
  loading,
  onChange,
  showValidationError,
}) => {
  const description = descriptions.find((d) => d.inputId === input.inputId);
  const nextdescription =
    nextInput === undefined
      ? undefined
      : descriptions.find((d) => d.inputId === nextInput.inputId);

  const currentAction =
    currentActionProp ?? actions.find((i) => i.hash === actionHash);

  useEffect(() => {
    if (
      description?.overrideValueConfig !== undefined &&
      description.display?.display.oneofKind === "hidden" &&
      input.value !== description.overrideValueConfig
    ) {
      onChange({
        ...input,
        value: description.overrideValueConfig,
      });
    }
  }, [description, input, onChange]);

  return (
    <StyledInputWrapper
      layout={description?.editor?.layout}
      nextInputDescription={
        description?.editor?.layout?.position === Position.SAME_LINE
          ? nextdescription
          : undefined
      }
    >
      {(() => {
        switch (description?.editor?.editor.oneofKind) {
          case "dynamicSelect":
            return (
              <DynamicSelectEditor
                description={description}
                editor={description.editor}
                config={{
                  ...input,
                  label: description.label,
                }}
                trigger={trigger}
                onChange={(input) => onChange(input)}
                showValidationError={Boolean(showValidationError)}
                actions={actions}
                action={currentAction}
                integrations={integrations}
                isMultiple={Boolean(
                  description.editor.editor.dynamicSelect.multiple,
                )}
              />
            );
          case "select":
          case "status":
          case "tag":
            return (
              <SelectEditor
                action={currentAction}
                trigger={trigger}
                showValidationError={Boolean(showValidationError)}
                disabled={loading}
                description={description}
                config={{
                  ...input,
                  label: description.label,
                }}
                onChange={(input) => onChange(input)}
              />
            );
          case "date":
          case "readOnly":
          case "email":
          case "text":
          case "keyValue":
          case "urlReadOnly":
            if (
              currentAction == null &&
              description.editor.editor.oneofKind === "readOnly"
            ) {
              return (
                <ReadOnlyEditor
                  input={toJS({
                    ...input,
                    label: description.label,
                  })}
                  onChange={onChange}
                  integrations={integrations}
                  description={description}
                  showValidationError={Boolean(showValidationError)}
                />
              );
            }
            if (
              currentAction == null &&
              description.editor.editor.oneofKind === "urlReadOnly"
            ) {
              return (
                <UrlReadOnlyEditor
                  input={toJS({
                    ...input,
                    label: description.label,
                  })}
                  onChange={onChange}
                  description={description}
                />
              );
            }
            if (
              currentAction == null &&
              description.editor.editor.oneofKind === "keyValue"
            ) {
              return (
                <KeyValueEditor
                  input={toJS({
                    ...input,
                    label: description.label,
                  })}
                  onChange={onChange}
                  trigger={trigger}
                  actions={actions}
                  integrations={integrations}
                  description={description}
                  showValidationError={true}
                />
              );
            }
            return (
              <FieldEditorRow
                input={{
                  ...input,
                  label: description.label,
                }}
                onChange={onChange}
                trigger={trigger}
                actions={actions}
                action={currentAction}
                integrations={integrations}
                showValidationError={Boolean(showValidationError)}
                inputDescription={description}
              />
            );
          case "code":
            return (
              <FieldEditorRow
                input={{
                  ...input,
                  label: description.label,
                }}
                onChange={onChange}
                trigger={trigger}
                actions={actions}
                action={currentAction}
                integrations={integrations}
                showValidationError={Boolean(showValidationError)}
                inputDescription={description}
              />
            );
          case "member":
            return (
              <MemberEditor
                description={description}
                config={{
                  ...input,
                  label: description.label,
                }}
                trigger={trigger}
                onChange={(input) => onChange(input)}
                showValidationError={Boolean(showValidationError)}
                actions={actions}
                action={currentAction}
                hideTeams={shouldHideTeams(description)}
                integrations={integrations}
                isMultiple={Boolean(
                  description.editor.editor?.oneofKind === "member"
                    ? description.editor.editor.member.multiple
                    : input.valueDescription?.multiple,
                )}
              />
            );
          case "phone":
            return (
              <PhoneEditor
                description={description}
                editor={description.editor}
                config={{
                  ...input,
                  label: description.label,
                }}
                integrations={integrations}
                trigger={trigger}
                onChange={(input) => onChange(input)}
                actions={actions}
                action={currentAction}
                isMultiple={Boolean(
                  description.editor.editor?.oneofKind === "phone"
                    ? description.editor.editor.phone.multiple
                    : input.valueDescription?.multiple,
                )}
              />
            );
          case "smartdoc":
            return (
              <FieldEditorRow
                key={`${actionHash}-${input.inputId}`}
                input={toJS({
                  ...input,
                  label: description.label,
                })}
                onChange={(input) => {
                  onChange(input);
                }}
                trigger={trigger}
                actions={actions}
                action={currentAction}
                integrations={integrations}
                showValidationError={Boolean(showValidationError)}
                // FieldEditorRow self-derives its description from `action`, but
                // `actions` is now the upstream-scoped list (spec 8.1) which
                // excludes the action being edited, so `currentAction` is
                // undefined. Pass the description explicitly (the fallback the
                // other FieldEditorRow cases already use) so the smartdoc editor
                // still renders. Without it the whole field disappears.
                inputDescription={description}
              />
            );
          case "file":
            return (
              <StyledReactSelectFieldContainer>
                <FileEditor
                  key={`${actionHash}-${input.inputId}`}
                  input={toJS({
                    ...input,
                    label: description.label,
                  })}
                  onChange={(input) => {
                    onChange(input);
                  }}
                  trigger={trigger}
                  description={description}
                  integrations={integrations}
                  action={currentAction}
                  actions={actions}
                />
              </StyledReactSelectFieldContainer>
            );
          case "outputs":
            return (
              <FieldsEditor
                input={toJS({
                  ...input,
                  label: description.label,
                })}
                onChange={onChange}
                trigger={trigger}
                description={description}
                integrations={integrations}
                action={currentAction}
              />
            );
          case "schedule":
            return (
              <ScheduleEditor
                input={toJS({
                  ...input,
                  label: description.label,
                })}
                onChange={onChange}
              />
            );
          case "checkbox":
            return (
              <CheckboxEditor
                input={toJS({
                  ...input,
                  label: description.label,
                })}
                onChange={onChange}
                description={description}
              />
            );
          case "limitAndSort":
            return (
              <LimitSortEditor
                input={toJS({
                  ...input,
                  label: description.label,
                })}
                onChange={onChange}
                action={currentAction}
                limitAndSortData={description.editor.editor.limitAndSort}
              />
            );
          case "mergeActions":
            return (
              <MergeActionsEditor
                description={description}
                onChange={(input) => {
                  onChange(input);
                }}
                config={{
                  ...input,
                  label: description.label,
                }}
              />
            );
          case "json":
          case "jsonStructure":
            return (
              <JsonEditorWrapper
                input={toJS({
                  ...input,
                  label: description.label,
                })}
                onChange={onChange}
                description={description}
                showValidationError={Boolean(showValidationError)}
                action={currentAction}
                actions={actions}
                integrations={integrations}
                trigger={trigger}
              />
            );
          case "testTrigger":
            return (
              <TestTriggerEditor
                input={toJS({
                  ...input,
                  label: description.label,
                })}
                onChange={onChange}
                trigger={trigger}
                integrations={integrations}
                description={description}
              />
            );
          case "hidden":
          default: {
            const editorKind = description?.editor?.editor.oneofKind;

            // Intentionally-hidden inputs, and inputs whose description hasn't
            // loaded yet, render nothing.
            if (editorKind === "hidden" || editorKind === undefined) {
              return null;
            }

            // Any other editor type is one this build doesn't know how to
            // render (e.g. a new backend editor). Surface it loudly instead of
            // dropping the input silently -- that's how the "code" editor went
            // unnoticed.
            // eslint-disable-next-line no-console
            console.error(
              `InputForm: no editor component for input "${input.inputId}" (editor type: "${editorKind}")`,
            );
            return (
              <UnknownInputEditor
                label={description?.label}
                editorKind={editorKind}
              />
            );
          }
        }
      })()}
    </StyledInputWrapper>
  );
};
